import "./polyfill";
import "@/blocks/Text";
import "@/components/Container";
import "@/components/Footer";
import "@/components/HomeAnimation";
import "@/components/Banner";
import "@/blocks/MediaAndCopy";
import "@/blocks/ImageWithText";
import "@/blocks/LogoGallery";
import "@/blocks/TechnologyImage";
import "@/components/Guide";
import "@/components/PrimaryButton";
import "@/components/NewsAndEvents";
import "@/components/NewsDetailsPage";
import "@/components/ShareLinks";
import "@/components/NewsAndEvents";
import "@/components/Resources";
import "@/components/Visualiser";
import "@/components/HomeNews";
import "@/components/Banner404";
import "@/components/ArticleHeader";
import "@/components/Tags";
import "@/components/ProgressBar";
import "@/components/TextGrid";
import "@/components/TextParagraph";
import "@/components/Flags";
import "@/components/TextWithLink";

// load global styles
import "@/styles/normalize.css";
import "@/styles/fonts.css";
import "@/styles/typography.css";
import "@/styles/common.css";
import "@/styles/privacy-policy.css";
import "@/styles/about.css";

import { laptop, tablet } from "./breakpoints";

import Anchor from "@/components/Anchor";
import Accordion from "@/components/Accordion";
import ContactForm from "@/components/ContactForm";
import CookieBanner from "@/components/CookieBanner";
import Guide from "@/components/Guide";
import Header from "@/components/Header";
import HomeAnimation from "@/components/HomeAnimation";
import NewsAndEvents from "@/components/NewsAndEvents";
import NewsDetailsPage from "@/components/NewsDetailsPage";
import Resources from "@/components/Resources";
import Banner404 from "@/components/Banner404";
import ShareLinks from "@/components/ShareLinks";
import Visualiser from "@/components/Visualiser";
import throttle from "lodash/throttle";
import TwitterFeed from "@/components/TwitterFeed";
import Technologies from "@/components/Technologies";
import TechnologyPage from "@/components/TechnologyPage";
import "@/components/TechnologyProgressBar";
import MindMap from "@/components/MindMap";
import MindMapControls from "@/components/MindMapControls";
import ContentPanel from "@/components/ContentPanel";
import TechnologyNavigator from "@/components/TechnologyNavigator";
import TechnologyNavigatorResults from "@/components/TechnologyNavigatorResults";
import "@/components/TechnologyRingSelector";

import TechnologySeach from "@/modals/TechnologySearch";

import {
  addClassTransition,
  removeClassTransition,
} from "@/utils/classTransition";

const MODULES = [
  Anchor,
  Accordion,
  Header,
  ContactForm,
  CookieBanner,
  Guide,
  HomeAnimation,
  NewsAndEvents,
  NewsDetailsPage,
  ShareLinks,
  Resources,
  Visualiser,
  Banner404,
  TwitterFeed,
  Technologies,
  TechnologyPage,
  TechnologyNavigator,
  TechnologyNavigatorResults,
  MindMap,
  MindMapControls,
  ContentPanel,
];

const MODALS = {
  technologysearch: TechnologySeach,
};

class App {
  lastIndex = 0;

  constructor() {
    window.addEventListener("DOMContentLoaded", this.initApp);
    window.addEventListener("resize", throttle(this.onResize, 150));
    window.addEventListener("scroll", throttle(this.onScroll, 150));
    window.addEventListener("popstate", this.onPopState);
  }

  initApp = () => {
    this.lastIndex = 0;
    this.currentView = this.getCurrentView();

    this.instances = [];
    this.modals = {};

    const main = document.querySelector("#main");
    if (main) {
      history.replaceState(
        {
          title: document.title,
          main: main.innerHTML,
          bodyClassAttribute: document.body.getAttribute("class"),
        },
        document.title,
        window.location.href
      );
    }

    this.initArea(document);
  };

  initClones = () => {
    this.instances = this.instances.concat(
      MODULES.flatMap((module) => {
        if (!module.hasOwnProperty("selector")) {
          return;
        }

        const blocks = [...document.querySelectorAll(module.selector)];
        return blocks
          .map((block) => {
            if (block.getAttribute("data-instance-index") !== "") {
              return null;
            }

            block.setAttribute("data-instance-index", this.lastIndex);
            this.lastIndex++;
            return new module(block);
          })
          .filter((v) => !!v);
      })
    );
  };

  onResize = (e) => {
    if (!this.instances) {
      return;
    }

    const currentView = this.getCurrentView();
    const changedView = this.currentView !== currentView;

    this.currentView = currentView;

    this.instances.forEach((instance) => {
      if (instance?.onResize) {
        instance.onResize(e, {
          changedView,
        });
      }
    });
  };

  getCurrentView() {
    const isTablet = window.innerWidth >= tablet;
    const isLaptop = window.innerWidth >= laptop;

    if (isLaptop) {
      return "laptop";
    }

    if (isTablet) {
      return "tablet";
    }

    return "mobile";
  }

  onScroll = (e) => {
    if (!this.instances) {
      return;
    }

    this.instances.forEach((instance) => {
      if (instance?.onScroll) {
        instance.onScroll(e);
      }
    });
  };

  initArea = (parentElement) => {
    if (!parentElement) {
      return [];
    }

    // Create module instances
    const newInstances = MODULES.filter((module) =>
      module.hasOwnProperty("selector")
    ).flatMap((Module) => {
      const blocks = [...parentElement.querySelectorAll(Module.selector)];
      return blocks
        .filter((block) => !block.getAttribute("data-instance-index"))
        .map((block) => {
          block.setAttribute("data-instance-index", this.lastIndex);

          this.lastIndex++;
          return new Module(block);
        });
    });

    // Create modals instances
    Object.keys(MODALS).forEach((k) => {
      const Modal = MODALS[k];

      if (!Modal.hasOwnProperty("selector")) {
        return;
      }

      const block = parentElement.querySelector(Modal.selector);
      if (!block) {
        return;
      }

      if (block.getAttribute("data-instance-index")) {
        return;
      }

      block.setAttribute("data-instance-index", this.lastIndex);
      this.lastIndex++;

      this.modals[k] = new Modal(block);

      newInstances.push(this.modals[k]);
    });

    // Create main module instances
    Object.keys(MODULES).forEach((k) => {
      const Module = MODULES[k];

      if (!Module.hasOwnProperty("selector")) {
        return;
      }

      const block = parentElement.querySelector(Module.selector);

      if (!block) {
        return;
      }

      if (block.getAttribute("data-instance-index")) {
        return;
      }

      block.setAttribute("data-instance-index", this.lastIndex);
      this.lastIndex++;

      this[k] = new Module(block);

      newInstances.push(this[k]);
    });

    // Call onReady method if any
    newInstances.forEach((instance) => {
      if (instance.onReady) {
        instance.onReady();
      }
    });

    // Call onComplete method if any
    newInstances.forEach((instance) => {
      if (instance.onComplete) {
        instance.onComplete();
      }
    });

    this.instances = this.instances.concat(newInstances);

    return newInstances;
  };

  destroyArea = (parentElement, keep = []) => {
    [...parentElement.children].forEach((element) => {
      // Skip destroy if element is in `keep`
      if (keep.some((s) => element.matches && element.matches(s))) {
        return;
      }

      // If this element has an instance attached
      if (element.getAttribute("data-instance-index")) {
        // Get instance
        const instance = getInstance(element);

        // Call onDestroy method if any
        if (instance?.onDestroy) {
          instance.onDestroy();
        }

        // Remove instance references
        this.instances[parseInt(element.dataset.instanceIndex, 10)] = null;
        Object.keys(MODULES).forEach((k) => {
          if (this[k] === instance) {
            this[k] = null;
          }
        });
        Object.keys(MODALS).forEach((k) => {
          if (this.modals[k] === instance) {
            this.modals[k] = null;
          }
        });
      }

      element.remove();
    });
  };

  fakeNavigateTo = async (url) => {
    // Update history
    history.pushState(
      {
        title: document.title,
        main: document.querySelector("#main").innerHTML,
        bodyClassAttribute: document.body.getAttribute("class"),
        isFake: true,
      },
      document.title,
      url
    );
  };

  navigateTo = async (url, _replaceSelectors = ["#main"], opts = {}) => {
    console.log(`Navigate to: ${url}`);

    const replaceSelectors = Array.isArray(_replaceSelectors)
      ? _replaceSelectors
      : [_replaceSelectors];

    const options = {
      keep: [],
      exitTransition: null,
      enterTransition: null,
      doScroll: true,
      ...opts,
    };

    // Async request
    const response = await fetch(url, {
      credentials: "include",
    });
    const html = await response.text();

    // Parse html received
    const parser = new DOMParser();
    const receivedDocument = parser.parseFromString(html, "text/html");

    const spinner = receivedDocument.querySelector(".content-panel__spinner");

    if (spinner) {
      spinner.classList.toggle("hidden");
    }

    // Copy content to
    const to = {};
    replaceSelectors.forEach((s) => (to[s] = document.querySelector(s)));

    const replaceableElements = Object.keys(to);

    if (!replaceableElements.length) {
      // There is nothing to replace
      return;
    }

    // Copy content from
    const from = {};
    replaceSelectors.forEach(
      (s) => (from[s] = receivedDocument.querySelector(s))
    );

    // Exit transition
    if (options.exitTransition) {
      await options.exitTransition();
    } else {
      const exitPromises = replaceableElements.map((k) =>
        addClassTransition(to[k], "hidden", {
          awaitForProperty: "opacity",
          timeout: 666,
        })
      );

      // Wait all the exiting in parallel
      await Promise.all(exitPromises);
    }

    // Destroy deleted content
    replaceableElements.forEach((k) => this.destroyArea(to[k], options.keep));

    if(receivedDocument.querySelector("#main") !== null ) {
    // Update history
    history.pushState(
      {
        title: receivedDocument.title,
        main: receivedDocument.querySelector("#main").innerHTML,
        bodyClassAttribute: receivedDocument.body.getAttribute("class"),
        isFake: false,
      },
      receivedDocument.title,
      response.url
    );
  }
    // Update title
    document.title = receivedDocument.title;

    // Get new content
    replaceableElements.forEach((k) => {
      if (!from[k]) {
        return;
      }

      while (from[k].childNodes.length > 0) {
        if (
          options.keep.some(
            (s) =>
              from[k].childNodes[0].matches && from[k].childNodes[0].matches(s)
          )
        ) {
          // If the head element is kept from previous content, just remove this
          // from the new content list and go ahead in the while
          from[k].childNodes[0].remove();
        } else {
          // If the head element is not kept from previous content, just detach
          // this from the new content list and append it to destination container
          // (`to`)
          to[k].appendChild(from[k].childNodes[0]);
        }
      }
    });

    if (options.doScroll) {
      // Scroll top
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }

    // Get new body classes
    document.body.setAttribute(
      "class",
      receivedDocument.body.getAttribute("class")
    );

    // Enter transition
    if (options.enterTransition) {
      await options.enterTransition();
    } else {
      const enteringPromises = replaceableElements.map((k) =>
        removeClassTransition(to[k], "hidden", {
          awaitForProperty: "opacity",
          timeout: 666,
        })
      );

      // Wait all the entering in parallel
      await Promise.all(enteringPromises);
    }

    // Initialize new content
    replaceableElements.forEach((k) => this.initArea(to[k]));

    if (options.event) {
      document
        .querySelector(options.eventTargetSelector)
        .dispatchEvent(options.event);
    }
  };

  onPopState = async (e) => {
    console.log(`Navigate back`, e);

    if (e.state !== null) {
      if ("main" in e.state) {
        const fromString = e.state.main;
        const to = document.querySelector("#main");

        // Exit transition
        await addClassTransition(to, "hidden", {
          awaitForProperty: "opacity",
          timeout: 400,
        });

        // destroy deleted content
        this.destroyArea(to);

        document.title = e.state.title;

        // Get new content
        to.innerHTML = fromString;

        // Get new body classes
        document.body.setAttribute("class", e.state.bodyClassAttribute);

        // Enter transition
        await removeClassTransition(to, "hidden", {
          awaitForProperty: "opacity",
          timeout: 400,
        });

        // Initialize new content
        this.initArea(to);
      }
    }
  };
}

window.App = new App();

export const getInstance = (el) => {
  const instanceIndex = el.getAttribute("data-instance-index");

  if (!instanceIndex) {
    return null;
  }

  return window.App.instances[parseInt(instanceIndex, 10)];
};

export const getInstances = (el) => {
  const instanceIndex = el.dataset.instanceIndex;

  if (!instanceIndex) {
    return [];
  }

  return instanceIndex
    .split(",")
    .map((i) => window.App.instances[parseInt(i, 10)]);
};
